import type {
  StormcloudVideoPlayerConfig,
  Scte35Marker,
  AdController,
} from "../types";
import {
  buildAnalyticsContext,
  sendAdLoadedTracking,
  sendAdImpressionTracking,
} from "../utils/tracking";
import type { AdTimingService } from "./AdTimingService";
import type { AdPreloadPool } from "./AdPreloadPool";
import type { AdConfigManager } from "./AdConfigManager";
import type { Scte35CueManager } from "./Scte35CueManager";
import type { PlaceholderLayer } from "./PlaceholderLayer";
import { getAdAudioVolume } from "./PlayerControls";

export interface AdBreakOrchestratorHost {
  readonly config: StormcloudVideoPlayerConfig;
  readonly video: HTMLVideoElement;
  getAdPlayer(): AdController;
  setAdPlayer(player: AdController): void;
  createAdPlayer(continueLiveStreamDuringAds: boolean): AdController;
  shouldContinueLiveStreamDuringAds(): boolean;
  getLiveSyncPosition(): number | undefined;
  generateVastUrls(baseUrl: string, count: number): string[];
  generatePodVastUrl(baseUrl: string, breakDurationMs?: number): string;
}

export class AdBreakOrchestrator {
  inAdBreak = false;
  showAds: boolean = false;
  isShowingPlaceholder: boolean = false;
  adDetectSentForCurrentBreak: boolean = false;

  private continuousFetchingActive: boolean = false;
  private continuousFetchLoopRunning = false;
  adRequestQueue: string[] = [];
  adPodQueue: string[] = [];
  private readonly maxPlaceholderDurationMs: number = 5000;

  pendingAdBreak: {
    marker: Scte35Marker;
    detectedAtFragmentSn?: number;
    vastUrls: string[];
    isFetching: boolean;
    fetchStartTime?: number;
  } | null = null;
  prefetchTimerId: number | undefined;

  private readonly host: AdBreakOrchestratorHost;
  private readonly timing: AdTimingService;
  private readonly preloadPool: AdPreloadPool;
  private readonly adConfig: AdConfigManager;
  private readonly cueManager: Scte35CueManager;
  private readonly placeholder: PlaceholderLayer;

  constructor(
    host: AdBreakOrchestratorHost,
    timing: AdTimingService,
    preloadPool: AdPreloadPool,
    adConfig: AdConfigManager,
    cueManager: Scte35CueManager,
    placeholder: PlaceholderLayer
  ) {
    this.host = host;
    this.timing = timing;
    this.preloadPool = preloadPool;
    this.adConfig = adConfig;
    this.cueManager = cueManager;
    this.placeholder = placeholder;
  }

  private get debug(): boolean {
    return !!this.host.config.debugAdTiming;
  }

  private get analyticsContext() {
    return buildAnalyticsContext(this.host.config);
  }

  // ───────────────────────────────────────────────────────────────
  //  IMA event listeners
  // ───────────────────────────────────────────────────────────────

  private get optimizedPodsEnabled(): boolean {
    return this.host.config.optimizedPods !== false;
  }

  attachImaEventListeners(): void {
    const adPlayer = this.host.getAdPlayer();
    adPlayer.on("pod_ad_started", (payload?: any) => {
      this.timing.notePodAdStarted();
      if (this.debug) {
        console.log(
          `[POD] Playing pod ad ${payload?.index != null ? payload.index + 1 : "?"}/${payload?.total ?? "?"}`
        );
      }
    });
    adPlayer.on("all_ads_completed", () => {
      sendAdImpressionTracking(
        this.host.config.licenseKey,
        { source: "hls", adIndex: this.timing.currentAdIndex, timestamp: new Date().toISOString() },
        this.analyticsContext
      ).catch(() => {});

      this.timing.recordPlayedAdDuration(this.host.getAdPlayer());
      const remaining = this.timing.getRemainingAdMs();

      this.timing.consecutiveFailures = 0;

      if (this.debug) {
        console.log(
          `[StormcloudVideoPlayer] all_ads_completed event received - remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}`,
          this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
        );
      }

      if (this.inAdBreak) {
        if (this.timing.hasTimeToStartAnotherAd()) {
          this.tryNextAvailableAdWithRateLimit();
        } else {
          this.handleAdPodComplete();
        }
      }
    });
    adPlayer.on("ad_error", (errorPayload?: any) => {
      let errorMessage = "Ad playback failed";

      if (errorPayload) {
        const errorCode = errorPayload.code || errorPayload.errorCode || "unknown";
        const vastErrorCode = errorPayload.vastErrorCode;
        const message = errorPayload.message || errorPayload.errorMessage || "Unknown error";
        const cause = errorPayload.cause || errorPayload.innerError || errorPayload.error;

        errorMessage = `Ad error: AdError ${errorCode}: ${message}`;

        if (vastErrorCode && vastErrorCode !== "N/A" && vastErrorCode !== errorCode) {
          errorMessage += ` (VAST Error Code: ${vastErrorCode})`;
        }

        if (cause) {
          const causeMessage = typeof cause === "string" ? cause : (cause.message || String(cause));
          errorMessage += `. Caused by: ${causeMessage}`;
        }
      }

      console.error("[AD-ERROR]", errorMessage, errorPayload || "");
      this.handleAdFailure();
    });
    adPlayer.on("content_pause", () => {
      this.timing.clearAdFailsafeTimer();
      this.timing.clearAdRequestWatchdog();
      this.timing.activeAdRequestToken = null;
      this.showAds = true;
      this.timing.resetGamNoFillCounter();

      if (this.inAdBreak && this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
        this.timing.scheduleAdStopAtBreakBoundary();
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Starting ad break timer on content_pause (first ad starting)");
        }
      }

      this.host.getAdPlayer().setAdVolume(getAdAudioVolume(this.host.getAdPlayer()));

      if (this.isShowingPlaceholder) {
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Hiding placeholder - new ads starting");
        }
        this.host.getAdPlayer().hidePlaceholder();
        this.isShowingPlaceholder = false;
      }
    });
    adPlayer.on("content_resume", () => {
      sendAdImpressionTracking(
        this.host.config.licenseKey,
        { source: "hls", adIndex: this.timing.currentAdIndex, timestamp: new Date().toISOString() },
        this.analyticsContext
      ).catch(() => {});

      if (!this.host.video.muted) {
        this.host.video.muted = true;
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Muted video on content_resume (ad ended, gap between ads)");
        }
      }

      this.timing.clearAdFailsafeTimer();
      this.timing.clearAdRequestWatchdog();
      this.timing.activeAdRequestToken = null;
      this.showAds = false;

      if (!this.inAdBreak) {
        return;
      }

      this.timing.consecutiveFailures = 0;

      this.timing.recordPlayedAdDuration(this.host.getAdPlayer());
      const remaining = this.timing.getRemainingAdMs();

      if (this.debug) {
        console.log(
          `[CONTINUOUS-FETCH] content_resume event: remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}`,
          this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
        );
      }

      if (this.timing.hasTimeToStartAnotherAd()) {
        this.tryNextAvailableAdWithRateLimit();
      } else {
        this.handleAdPodComplete();
      }
    });
  }

  recreateAdController(): void {
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] Recreating AdController for new ad");
    }

    const shouldShowPlaceholder = this.inAdBreak && this.showAds;
    const adPlayer = this.host.getAdPlayer();
    if (shouldShowPlaceholder && adPlayer) {
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Showing placeholder before destroying old AdController");
      }
      this.placeholder.show();
      adPlayer.showPlaceholder();
      this.isShowingPlaceholder = true;
    }

    const preservedMutedState = adPlayer.getOriginalMutedState();
    const preservedVolumeState = adPlayer.getOriginalVolume();

    if (adPlayer) {
      try {
        adPlayer.destroy();
        this.host.video.muted = true;
      } catch (error) {
        if (this.debug) {
          console.warn("[StormcloudVideoPlayer] Error destroying old AdController:", error);
        }
      }
    }

    const continueLiveStreamDuringAds = this.host.shouldContinueLiveStreamDuringAds();
    const newAdPlayer = this.host.createAdPlayer(continueLiveStreamDuringAds);
    this.host.setAdPlayer(newAdPlayer);

    newAdPlayer.initialize();
    newAdPlayer.updateOriginalMutedState(preservedMutedState, preservedVolumeState);
    this.attachImaEventListeners();

    if (shouldShowPlaceholder) {
      this.placeholder.show();
      newAdPlayer.showPlaceholder();
      this.isShowingPlaceholder = true;
    }
  }

  clearPendingAdBreak(): void {
    if (this.prefetchTimerId != null) {
      clearTimeout(this.prefetchTimerId);
      this.prefetchTimerId = undefined;
    }
    this.pendingAdBreak = null;
    this.cueManager.pendingScte35CueKey = undefined;
    this.adConfig.podAssignedByPrefetch = false;
  }

  // ───────────────────────────────────────────────────────────────
  //  Ad break start
  // ───────────────────────────────────────────────────────────────

  async handleAdStart(_marker: Scte35Marker): Promise<void> {
    const scheduled = this.adConfig.findCurrentOrNextBreak(
      this.host.video.currentTime * 1000
    );
    if (scheduled) {
      this.adConfig.consumedVmapBreakIds.add(this.adConfig.getAdBreakKey(scheduled));
    }
    const tags = this.adConfig.selectVastTagsForBreak(scheduled);

    let baseVastUrl: string;

    if (tags && tags.length > 0 && tags[0]) {
      baseVastUrl = tags[0];
    } else if (this.adConfig.apiVastTagUrl) {
      baseVastUrl = this.adConfig.apiVastTagUrl;
    } else {
      this.clearPendingAdBreak();
      return;
    }

    const adBreakDurationMs =
      _marker.durationSeconds != null
        ? _marker.durationSeconds * 1000
        : scheduled?.durationMs;

    if (this.debug) {
      const mode = this.host.config.isLiveStream ? "LIVE" : "VOD";
      console.log(
        `[CONTINUOUS-FETCH] ${mode} MODE: Target duration=${adBreakDurationMs}ms`
      );
    }

    this.timing.failedVastUrls.clear();
    this.timing.temporaryFailureUrls.clear();
    this.timing.consecutiveFailures = 0;
    this.continuousFetchingActive = true;
    this.isShowingPlaceholder = false;
    this.timing.totalAdRequestsInBreak = 0;

    if (this.adConfig.podAssignedByPrefetch) {
      this.adConfig.podAssignedByPrefetch = false;
    } else {
      this.adConfig.beginNewAdPod();
    }

    const currentMuted = this.host.video.muted;
    const currentVolume = this.host.video.volume;
    this.host.getAdPlayer().updateOriginalMutedState(currentMuted, currentVolume);

    this.inAdBreak = true;
    this.timing.adBreakPlayedDurationMs = 0;
    this.timing.currentAdIndex = 0;
    this.timing.totalAdsInBreak = 0;
    this.adPodQueue = [];
    this.showAds = false;

    if (
      this.timing.expectedAdBreakDurationMs == null &&
      adBreakDurationMs != null
    ) {
      this.timing.expectedAdBreakDurationMs = adBreakDurationMs;
    }

    if (this.optimizedPodsEnabled) {
      const podPlayed = await this.startOptimizedPod(
        baseVastUrl,
        adBreakDurationMs,
        currentMuted,
        currentVolume
      );
      if (podPlayed) {
        return;
      }
      if (this.debug) {
        console.log("[POD] Optimized pod unavailable, falling back to sequential single-ad path");
      }
    }

    let firstAdUrl: string | undefined;
    let usePreloadedAd = false;
    let preloadedController: AdController | undefined;

    const preloaded = this.preloadPool.getPreloadedAd();
    if (preloaded) {
      firstAdUrl = preloaded.vastUrl;
      preloadedController = preloaded.adController;
      usePreloadedAd = true;

      sendAdLoadedTracking(
        this.host.config.licenseKey,
        { source: "hls", vastUrl: firstAdUrl, timestamp: new Date().toISOString() },
        this.analyticsContext
      ).catch(() => {});
      if (this.debug) {
        console.log("[CONTINUOUS-FETCH] Using preloaded ad from pool (preloaded in advance, ready immediately!)");
      }
    } else if (this.pendingAdBreak && this.pendingAdBreak.vastUrls.length > 0) {
      this.adRequestQueue = [...this.pendingAdBreak.vastUrls];
      firstAdUrl = this.adRequestQueue.shift();

      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Pool empty, using prefetched URLs (${this.adRequestQueue.length + 1} available)`);
      }

      this.clearPendingAdBreak();
    } else {
      if (this.debug) {
        console.log("[CONTINUOUS-FETCH] Generating new ad URLs...");
      }

      const firstAdUrlArray = this.host.generateVastUrls(baseVastUrl, 1);
      firstAdUrl = firstAdUrlArray[0];
      this.adRequestQueue = [];
    }

    if (!firstAdUrl) {
      if (this.debug) {
        console.warn("[CONTINUOUS-FETCH] Failed to get first ad URL");
      }
      this.handleAdPodComplete();
      return;
    }

    if (this.debug) {
      console.log("[CONTINUOUS-FETCH] First VAST URL:", firstAdUrl);
    }

    try {
      if (usePreloadedAd && preloadedController) {
        if (!this.timing.ensureLoadedAdFitsBudget(preloadedController)) {
          this.timing.rejectLoadedAdForDuration(preloadedController);
          this.startContinuousFetching(baseVastUrl);
          await this.tryNextAvailableAdWithRateLimit();
          return;
        }

        const adPlayer = this.host.getAdPlayer();
        const preservedMuted = adPlayer.getOriginalMutedState();
        const preservedVolume = adPlayer.getOriginalVolume();
        adPlayer.destroy();
        this.host.video.muted = true;
        this.host.setAdPlayer(preloadedController);
        preloadedController.updateOriginalMutedState(preservedMuted, preservedVolume);

        this.attachImaEventListeners();

        if (this.debug) {
          console.log("[CONTINUOUS-FETCH] Using preloaded ad, resuming and starting playback immediately (no request delay!)");
        }

        preloadedController.resume();

        this.timing.consecutiveFailures = 0;
        this.startContinuousFetching(baseVastUrl);

        if (!this.preloadPool.active) {
          this.preloadPool.active = true;
        }
        if (!this.preloadPool.isLoopRunning) {
          this.preloadPool.preloadPoolLoop(baseVastUrl);
        }

        await preloadedController.play();
        this.timing.markAdStarted();

        if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
          this.timing.scheduleAdStopAtBreakBoundary();
        }

        const adVolume = currentMuted ? 0 : currentVolume;
        preloadedController.setAdVolume(adVolume);
      } else {
        const adPlayer = this.host.getAdPlayer();
        await this.timing.enforceGlobalRateLimit();
        this.timing.lastAdRequestTime = Date.now();
        await adPlayer.requestAds(firstAdUrl);
        sendAdLoadedTracking(
          this.host.config.licenseKey,
          { source: "hls", vastUrl: firstAdUrl, timestamp: new Date().toISOString() },
          this.analyticsContext
        ).catch(() => {});

        if (this.debug) {
          console.log("[CONTINUOUS-FETCH] First ad request successful, starting playback");
        }

        if (!this.timing.ensureLoadedAdFitsBudget(adPlayer)) {
          this.timing.rejectLoadedAdForDuration(adPlayer);
          this.recreateAdController();
          this.startContinuousFetching(baseVastUrl);
          await this.tryNextAvailableAdWithRateLimit();
          return;
        }

        this.timing.consecutiveFailures = 0;
        this.startContinuousFetching(baseVastUrl);

        if (!this.preloadPool.active) {
          this.preloadPool.startPreloadPool(baseVastUrl, []);
        }

        await adPlayer.play();
        this.timing.markAdStarted();

        if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
          this.timing.scheduleAdStopAtBreakBoundary();
        }

        const adVolume = currentMuted ? 0 : currentVolume;
        adPlayer.setAdVolume(adVolume);
      }
    } catch (error) {
      if (this.debug) {
        console.warn("[CONTINUOUS-FETCH] First ad request failed:", error);
      }

      if (!usePreloadedAd) {
        const fallbackPreloaded = this.preloadPool.getPreloadedAd();
        if (fallbackPreloaded) {
          if (this.debug) {
            console.log("[CONTINUOUS-FETCH] First ad failed, using preloaded fallback");
          }

          try {
            if (!this.timing.ensureLoadedAdFitsBudget(fallbackPreloaded.adController)) {
              this.timing.rejectLoadedAdForDuration(fallbackPreloaded.adController);
              this.startContinuousFetching(baseVastUrl);
              await this.tryNextAvailableAdWithRateLimit();
              return;
            }

            this.swapToPreloadedAdPlayer(fallbackPreloaded.adController);

            this.timing.consecutiveFailures = 0;
            this.startContinuousFetching(baseVastUrl);

            if (!this.preloadPool.active) {
              this.preloadPool.active = true;
              this.preloadPool.preloadPoolLoop(baseVastUrl);
            }

            const ap = this.host.getAdPlayer();
            await ap.play();
            this.timing.markAdStarted();

            if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
              this.timing.scheduleAdStopAtBreakBoundary();
            }

            const adVolume = currentMuted ? 0 : currentVolume;
            ap.setAdVolume(adVolume);

            return;
          } catch (fallbackError) {
            if (this.debug) {
              console.warn("[CONTINUOUS-FETCH] Preloaded fallback also failed:", fallbackError);
            }
          }
        }
      }

      if (this.timing.isTemporaryAdError(error as Error)) {
        this.timing.temporaryFailureUrls.set(firstAdUrl, Date.now());
        if (this.debug) {
          console.log("[CONTINUOUS-FETCH] First ad had temporary error (no-fill) - will try alternatives");
        }
      } else {
        this.timing.failedVastUrls.add(firstAdUrl);
        if (this.debug) {
          console.log("[CONTINUOUS-FETCH] First ad had permanent error - URL blacklisted");
        }
      }
      this.timing.consecutiveFailures++;

      this.startContinuousFetching(baseVastUrl);
      await this.tryNextAvailableAdWithRateLimit();
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Optimized pod (single multi-ad request)
  // ───────────────────────────────────────────────────────────────

  private async startOptimizedPod(
    baseVastUrl: string,
    breakDurationMs: number | undefined,
    currentMuted: boolean,
    currentVolume: number
  ): Promise<boolean> {
    let podUrl: string | undefined;
    if (this.pendingAdBreak && this.pendingAdBreak.vastUrls.length > 0) {
      podUrl = this.pendingAdBreak.vastUrls[0];
      this.clearPendingAdBreak();
    } else {
      podUrl = this.host.generatePodVastUrl(baseVastUrl, breakDurationMs);
    }

    if (!podUrl) {
      return false;
    }

    this.continuousFetchingActive = false;

    this.recreateAdController();

    const adPlayer = this.host.getAdPlayer();
    const requestToken = ++this.timing.adRequestTokenCounter;
    this.timing.activeAdRequestToken = requestToken;
    this.timing.startAdRequestWatchdog(requestToken);

    try {
      await this.timing.enforceGlobalRateLimit();
      this.timing.lastAdRequestTime = Date.now();
      await adPlayer.requestAds(podUrl);
      this.timing.clearAdRequestWatchdog();

      if (this.timing.activeAdRequestToken !== requestToken) {
        return true;
      }

      const podCount = adPlayer.getPodAdCount?.() ?? 1;
      if (podCount < 1) {
        return false;
      }

      if (this.debug) {
        console.log(
          podCount === 1
            ? "[POD] Response contained a single ad; playing it and enabling sequential top-up"
            : `[POD] Optimized pod returned ${podCount} ads; playing pod in one controller`
        );
      }

      sendAdLoadedTracking(
        this.host.config.licenseKey,
        { source: "hls", vastUrl: podUrl, timestamp: new Date().toISOString() },
        this.analyticsContext
      ).catch(() => {});

      this.timing.startAdFailsafeTimer(requestToken);
      await adPlayer.play();
      this.timing.markAdStarted();

      if (
        this.timing.expectedAdBreakDurationMs != null &&
        this.timing.getAdBreakEndWallClockMs() == null
      ) {
        this.timing.scheduleAdStopAtBreakBoundary();
      }

      adPlayer.setAdVolume(currentMuted ? 0 : currentVolume);
      this.timing.consecutiveFailures = 0;

      if (podCount === 1) {
        this.continuousFetchingActive = true;
        this.startContinuousFetching(baseVastUrl);
        if (!this.preloadPool.active) {
          this.preloadPool.startPreloadPool(baseVastUrl, []);
        }
      }

      return true;
    } catch (error) {
      this.timing.clearAdRequestWatchdog();
      this.timing.clearAdFailsafeTimer();
      if (this.timing.activeAdRequestToken === requestToken) {
        this.timing.activeAdRequestToken = null;
      }
      if (this.debug) {
        console.warn("[POD] Optimized pod request/play failed:", error);
      }

      if (this.timing.isTemporaryAdError(error as Error)) {
        this.timing.temporaryFailureUrls.set(podUrl, Date.now());
      }

      this.continuousFetchingActive = true;
      this.recreateAdController();
      return false;
    }
  }

  private swapToPreloadedAdPlayer(preloadedController: AdController): void {
    const adPlayer = this.host.getAdPlayer();
    const preservedMuted = adPlayer.getOriginalMutedState();
    const preservedVolume = adPlayer.getOriginalVolume();
    adPlayer.destroy();
    this.host.video.muted = true;
    this.host.setAdPlayer(preloadedController);
    this.attachImaEventListeners();
    preloadedController.updateOriginalMutedState(preservedMuted, preservedVolume);
    preloadedController.resume();
  }

  // ───────────────────────────────────────────────────────────────
  //  Continuous fetching
  // ───────────────────────────────────────────────────────────────

  private startContinuousFetching(baseVastUrl: string): void {
    if (!this.continuousFetchingActive) {
      return;
    }

    if (this.continuousFetchLoopRunning) {
      if (this.debug) {
        console.log("[CONTINUOUS-FETCH] Fetch loop already running, skipping duplicate start");
      }
      return;
    }

    if (this.debug) {
      console.log("[CONTINUOUS-FETCH] Starting continuous ad fetching loop");
    }

    this.continuousFetchLoopRunning = true;
    this.continuousFetchLoop(baseVastUrl);
  }

  private async continuousFetchLoop(baseVastUrl: string): Promise<void> {
    const epoch = this.timing.adBreakEpoch;
    while (
      this.continuousFetchingActive &&
      this.inAdBreak &&
      epoch === this.timing.adBreakEpoch
    ) {
      const remaining = Math.min(
        this.timing.getWallClockRemainingAdMs(),
        this.timing.getDurationBudgetRemainingMs()
      );

      if (remaining <= this.timing.getMinRemainingMsToStartAd()) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] Not enough ad break time left (wall=${this.timing.getWallClockRemainingAdMs()}ms, budget=${this.timing.getDurationBudgetRemainingMs()}ms), stopping URL generation`);
        }
        break;
      }

      if (this.timing.isGamInCooldown()) {
        if (this.debug) {
          console.log("[CIRCUIT-BREAKER] GAM in cooldown, stopping URL generation");
        }
        break;
      }

      if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] Too many consecutive failures (${this.timing.consecutiveFailures}), stopping URL generation`);
        }
        break;
      }

      if (this.timing.totalAdRequestsInBreak >= this.timing.maxTotalAdRequestsPerBreak) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] Maximum ad requests reached (${this.timing.maxTotalAdRequestsPerBreak}), stopping`);
        }
        break;
      }

      const maxQueueSize = 5;
      if (this.adRequestQueue.length >= maxQueueSize) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] URL queue full (${this.adRequestQueue.length}), waiting...`);
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }

      const newAdUrl = this.host.generateVastUrls(baseVastUrl, 1)[0];

      if (!newAdUrl || this.timing.failedVastUrls.has(newAdUrl) || this.timing.isUrlInCooldown(newAdUrl)) {
        await new Promise(resolve => setTimeout(resolve, 500));
        continue;
      }

      if (this.debug) {
        const queuedUrlsPreview = [...this.adRequestQueue, newAdUrl];
        console.log(
          `[CONTINUOUS-FETCH] Queued VAST URL (queue size: ${queuedUrlsPreview.length})`,
          { queuedUrls: queuedUrlsPreview }
        );
      }

      this.adRequestQueue.push(newAdUrl);

      const generationDelay = this.timing.consecutiveFailures > 0
        ? Math.min(1000 * Math.pow(2, this.timing.consecutiveFailures), 5000)
        : 500;
      await new Promise(resolve => setTimeout(resolve, generationDelay));
    }

    this.continuousFetchLoopRunning = false;

    if (this.debug) {
      console.log(
        `[CONTINUOUS-FETCH] URL generation loop ended (queued: ${this.adRequestQueue.length})`,
        this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
      );
    }
  }

  stopContinuousFetching(): void {
    this.continuousFetchingActive = false;
    this.continuousFetchLoopRunning = false;

    this.placeholder.hide();

    if (this.debug) {
      console.log("[CONTINUOUS-FETCH] Stopping continuous ad fetching");
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Ad iteration
  // ───────────────────────────────────────────────────────────────

  async tryNextAvailableAdWithRateLimit(): Promise<void> {
    if (!this.inAdBreak) {
      return;
    }

    const epoch = this.timing.adBreakEpoch;

    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Too many consecutive failures (${this.timing.consecutiveFailures}), ending ad break gracefully`);
      }
      this.handleAdPodComplete();
      return;
    }

    const backoffMultiplier = Math.pow(2, this.timing.consecutiveFailures);
    const backoffDelay = Math.min(this.timing.backoffBaseMs * backoffMultiplier, this.timing.maxBackoffMs);
    const effectiveMinInterval = this.timing.minAdRequestIntervalMs + (this.timing.consecutiveFailures > 0 ? backoffDelay : 0);

    const timeSinceLastRequest = Date.now() - this.timing.lastAdRequestTime;
    if (timeSinceLastRequest < effectiveMinInterval) {
      const waitTime = effectiveMinInterval - timeSinceLastRequest;
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Rate limiting: waiting ${waitTime}ms before next request (backoff: ${this.timing.consecutiveFailures} failures)`);
      }
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    if (!this.inAdBreak || epoch !== this.timing.adBreakEpoch) {
      return;
    }

    return this.tryNextAvailableAd(0);
  }

  private async tryNextAvailableAd(retryCount: number = 0): Promise<void> {
    if (!this.inAdBreak) {
      return;
    }

    const remaining = this.timing.getRemainingAdMs();

    if (!this.timing.hasTimeToStartAnotherAd()) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Not enough time remaining (${remaining}ms) to start another ad, ending ad break`);
      }
      this.handleAdPodComplete();
      return;
    }

    if (this.timing.isGamInCooldown()) {
      if (this.debug) {
        console.log("[CIRCUIT-BREAKER] GAM in cooldown, ending ad break gracefully");
      }
      this.handleAdPodComplete();
      return;
    }

    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Too many consecutive failures (${this.timing.consecutiveFailures}), ending ad break`);
      }
      this.handleAdPodComplete();
      return;
    }

    this.timing.cleanupTemporaryFailures();

    const preloaded = this.preloadPool.getPreloadedAd();
    if (preloaded) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Using preloaded ad from pool`);
      }

      sendAdLoadedTracking(
        this.host.config.licenseKey,
        { source: "hls", vastUrl: preloaded.vastUrl, timestamp: new Date().toISOString() },
        this.analyticsContext
      ).catch(() => {});

      try {
        if (!this.timing.ensureLoadedAdFitsBudget(preloaded.adController)) {
          this.timing.rejectLoadedAdForDuration(preloaded.adController);
          await this.tryNextAvailableAdWithRateLimit();
          return;
        }

        this.swapToPreloadedAdPlayer(preloaded.adController);

        const ap = this.host.getAdPlayer();
        await ap.play();
        this.timing.markAdStarted();

        if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
          this.timing.scheduleAdStopAtBreakBoundary();
        }

        ap.setAdVolume(getAdAudioVolume(ap));

        this.timing.consecutiveFailures = 0;
        return;
      } catch (error) {
        if (this.debug) {
          console.warn("[CONTINUOUS-FETCH] Preloaded ad failed to play:", error);
        }
      }
    }

    while (this.adRequestQueue.length > 0) {
      const nextAdUrl = this.adRequestQueue.shift();

      if (!nextAdUrl) continue;

      if (this.timing.failedVastUrls.has(nextAdUrl)) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Skipping permanently-failed URL, trying next...");
        continue;
      }

      if (this.timing.isUrlInCooldown(nextAdUrl)) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Skipping URL in cooldown (recent no-fill), trying next...");
        continue;
      }

      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Requesting next ad via HLS ad player (${this.timing.currentAdIndex + 1}, ${this.adRequestQueue.length} remaining in queue)`);
      }

      try {
        await this.playSingleAd(nextAdUrl);
        this.timing.consecutiveFailures = 0;
      } catch (error) {
        const errorMessage = (error as Error).message;
        if (this.debug) console.log("[CONTINUOUS-FETCH] Ad request failed:", errorMessage);

        if (errorMessage.includes("ad_duration_exceeds_budget")) {
          await this.tryNextAvailableAdWithRateLimit();
          return;
        }

        if (!errorMessage.includes("permanently failed") && !errorMessage.includes("in cooldown")) {
          this.timing.consecutiveFailures++;
        }

        await this.tryNextAvailableAdWithRateLimit();
      }
      return;
    }

    const maxRetries = 3;
    if (this.continuousFetchingActive && retryCount < maxRetries && remaining > 2000) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Queue empty, waiting for URLs... (${retryCount + 1}/${maxRetries})`);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      await this.tryNextAvailableAd(retryCount + 1);
      return;
    }

    if (!this.isShowingPlaceholder && remaining > 1000) {
      this.showPlaceholderAndWaitForAds();
    } else {
      if (this.debug) console.log("[CONTINUOUS-FETCH] No more ads available, ending ad break");
      this.handleAdPodComplete();
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Single ad playback
  // ───────────────────────────────────────────────────────────────

  async playSingleAd(vastTagUrl: string): Promise<void> {
    const adPlayer = this.host.getAdPlayer();
    if (adPlayer.isAdPlaying()) return;

    if (this.timing.failedVastUrls.has(vastTagUrl)) {
      if (this.debug) console.warn("[AD-ERROR] Skipping permanently-failed VAST URL:", vastTagUrl);
      throw new Error("VAST URL permanently failed");
    }

    if (this.timing.isUrlInCooldown(vastTagUrl)) {
      if (this.debug) console.warn("[AD-ERROR] Skipping URL in cooldown (recent no-fill):", vastTagUrl);
      throw new Error("VAST URL in cooldown after no-fill");
    }

    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) console.warn("[AD-ERROR] Too many consecutive failures, not requesting more ads");
      throw new Error("Too many consecutive failures");
    }

    if (this.timing.isGamInCooldown()) {
      if (this.debug) console.warn("[CIRCUIT-BREAKER] GAM in cooldown, skipping ad request");
      throw new Error("GAM in cooldown");
    }

    this.recreateAdController();

    const requestToken = ++this.timing.adRequestTokenCounter;
    this.timing.activeAdRequestToken = requestToken;
    this.timing.startAdRequestWatchdog(requestToken);

    try {
      await this.timing.enforceGlobalRateLimit();
      this.timing.lastAdRequestTime = Date.now();
      const ap = this.host.getAdPlayer();
      await ap.requestAds(vastTagUrl);
      sendAdLoadedTracking(
        this.host.config.licenseKey,
        { source: "hls", vastUrl: vastTagUrl, timestamp: new Date().toISOString() },
        this.analyticsContext
      ).catch(() => {});

      this.timing.clearAdRequestWatchdog();

      if (this.timing.activeAdRequestToken !== requestToken) return;

      if (!this.timing.ensureLoadedAdFitsBudget(ap)) {
        this.timing.rejectLoadedAdForDuration(ap);
        this.recreateAdController();
        throw new Error("ad_duration_exceeds_budget");
      }

      try {
        this.timing.startAdFailsafeTimer(requestToken);
        await ap.play();
        this.timing.markAdStarted();

        if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
          this.timing.scheduleAdStopAtBreakBoundary();
        }

        ap.setAdVolume(getAdAudioVolume(ap));
        this.timing.consecutiveFailures = 0;
        this.timing.temporaryFailureUrls.delete(vastTagUrl);
      } catch (playError) {
        if (this.debug) console.error("[AD-ERROR] Failed to play ad:", playError);

        const preloadedFallback = this.preloadPool.getPreloadedAd();
        if (preloadedFallback) {
          if (this.debug) console.log("[AD-ERROR] Play failed, trying preloaded fallback ad");
          try {
            if (!this.timing.ensureLoadedAdFitsBudget(preloadedFallback.adController)) {
              this.timing.rejectLoadedAdForDuration(preloadedFallback.adController);
              throw playError;
            }
            this.timing.clearAdFailsafeTimer();
            this.swapToPreloadedAdPlayer(preloadedFallback.adController);
            this.timing.consecutiveFailures = 0;
            const fbAp = this.host.getAdPlayer();
            await fbAp.play();
            this.timing.markAdStarted();
            if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
              this.timing.scheduleAdStopAtBreakBoundary();
            }
            fbAp.setAdVolume(getAdAudioVolume(fbAp));
            return;
          } catch (fallbackError) {
            if (this.debug) console.warn("[AD-ERROR] Preloaded fallback also failed:", fallbackError);
          }
        }

        if (this.timing.isTemporaryAdError(playError as Error)) {
          this.timing.temporaryFailureUrls.set(vastTagUrl, Date.now());
          if (this.debug) console.log("[AD-ERROR] Temporary play error - URL can be retried after cooldown");
        } else {
          this.timing.failedVastUrls.add(vastTagUrl);
          if (this.debug) console.log("[AD-ERROR] Permanent play error - URL blacklisted");
        }

        this.timing.clearAdFailsafeTimer();
        if (this.timing.activeAdRequestToken === requestToken) {
          this.timing.activeAdRequestToken = null;
        }
        throw playError;
      }
    } catch (error) {
      const errorMessage = (error as Error)?.message || "";
      if (this.debug) console.error("[AD-ERROR] Ad request failed:", errorMessage);

      const preloadedFallback = this.preloadPool.getPreloadedAd();
      if (preloadedFallback) {
        if (this.debug) console.log("[AD-ERROR] Request failed, trying preloaded fallback ad");
        try {
          if (!this.timing.ensureLoadedAdFitsBudget(preloadedFallback.adController)) {
            this.timing.rejectLoadedAdForDuration(preloadedFallback.adController);
            throw error;
          }
          this.timing.clearAdRequestWatchdog();
          this.timing.clearAdFailsafeTimer();
          this.swapToPreloadedAdPlayer(preloadedFallback.adController);
          this.timing.consecutiveFailures = 0;
          const fbAp = this.host.getAdPlayer();
          await fbAp.play();
          this.timing.markAdStarted();
          fbAp.setAdVolume(getAdAudioVolume(fbAp));
          return;
        } catch (fallbackError) {
          if (this.debug) console.warn("[AD-ERROR] Preloaded fallback also failed:", fallbackError);
        }
      }

      if (this.timing.isTemporaryAdError(error as Error)) {
        this.timing.temporaryFailureUrls.set(vastTagUrl, Date.now());
        if (this.debug) console.log("[AD-ERROR] Temporary error (no-fill/timeout) - URL can be retried after cooldown");
      } else {
        this.timing.failedVastUrls.add(vastTagUrl);
        if (this.debug) console.log("[AD-ERROR] Permanent error - URL blacklisted:", errorMessage);
      }

      this.timing.clearAdRequestWatchdog();
      this.timing.clearAdFailsafeTimer();
      if (this.timing.activeAdRequestToken === requestToken) {
        this.timing.activeAdRequestToken = null;
      }
      throw error;
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Placeholder wait
  // ───────────────────────────────────────────────────────────────

  private async showPlaceholderAndWaitForAds(): Promise<void> {
    const remaining = this.timing.getRemainingAdMs();
    const waitTime = Math.min(this.maxPlaceholderDurationMs, remaining);

    if (this.timing.isGamInCooldown()) {
      if (this.debug) console.log("[CIRCUIT-BREAKER] GAM in cooldown, skipping placeholder wait");
      this.handleAdPodComplete();
      return;
    }

    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) console.log("[CONTINUOUS-FETCH] Skipping placeholder - too many consecutive failures");
      this.handleAdPodComplete();
      return;
    }

    if (waitTime < 1000) {
      this.handleAdPodComplete();
      return;
    }

    if (this.debug) {
      console.log(`[CONTINUOUS-FETCH] Showing placeholder for ${waitTime}ms while waiting for ad URLs`);
    }

    this.isShowingPlaceholder = true;
    this.host.getAdPlayer().showPlaceholder();

    const checkInterval = 300;
    const maxChecks = Math.floor(waitTime / checkInterval);

    for (let i = 0; i < maxChecks; i++) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));

      if (!this.inAdBreak) return;

      if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Too many failures during placeholder wait");
        break;
      }

      if (!this.timing.hasTimeToStartAnotherAd()) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Not enough time remaining during placeholder wait, ending ad break");
        break;
      }

      while (this.adRequestQueue.length > 0) {
        if (!this.timing.hasTimeToStartAnotherAd()) break;

        const nextAdUrl = this.adRequestQueue.shift();
        if (!nextAdUrl) continue;
        if (this.timing.failedVastUrls.has(nextAdUrl)) continue;
        if (this.timing.isUrlInCooldown(nextAdUrl)) continue;

        if (this.debug) console.log("[CONTINUOUS-FETCH] Ad URL available, requesting via HLS ad player");

        this.isShowingPlaceholder = false;
        this.host.getAdPlayer().hidePlaceholder();

        try {
          await this.playSingleAd(nextAdUrl);
          this.timing.consecutiveFailures = 0;
        } catch (error) {
          const errorMessage = (error as Error).message;
          if (errorMessage.includes("ad_duration_exceeds_budget")) {
            await this.tryNextAvailableAdWithRateLimit();
            return;
          }
          if (!errorMessage.includes("permanently failed") && !errorMessage.includes("in cooldown")) {
            this.timing.consecutiveFailures++;
          }
          await this.tryNextAvailableAdWithRateLimit();
        }
        return;
      }
    }

    if (this.debug) console.log("[CONTINUOUS-FETCH] Placeholder timeout, ending ad break");

    this.isShowingPlaceholder = false;
    this.host.getAdPlayer().hidePlaceholder();
    this.handleAdPodComplete();
  }

  // ───────────────────────────────────────────────────────────────
  //  Ad break completion
  // ───────────────────────────────────────────────────────────────

  handleAdPodComplete(): void {
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] Ad pod complete - cleaning up");
    }

    this.timing.resetForBreakEnd();

    this.stopContinuousFetching();
    this.preloadPool.stop();
    this.clearPendingAdBreak();

    if (this.isShowingPlaceholder) {
      this.host.getAdPlayer().hidePlaceholder();
      this.isShowingPlaceholder = false;
    }

    this.adRequestQueue = [];

    this.inAdBreak = false;
    this.adDetectSentForCurrentBreak = false;
    this.cueManager.activeScte35BreakKey = undefined;
    this.cueManager.scheduledScte35BreakKey = undefined;
    this.adPodQueue = [];
    this.showAds = false;

    this.host.getAdPlayer().stop().catch(() => {});

    const restoredMuted = this.host.getAdPlayer().getOriginalMutedState();
    const restoredVolume = this.host.getAdPlayer().getOriginalVolume();

    if (this.host.video.muted !== restoredMuted) {
      this.host.video.muted = restoredMuted;
    }
    if (Math.abs(this.host.video.volume - restoredVolume) > 0.01) {
      this.host.video.volume = restoredVolume;
    }

    if (this.host.shouldContinueLiveStreamDuringAds()) {
      const liveSyncPos = this.host.getLiveSyncPosition();
      if (liveSyncPos != null && liveSyncPos > this.host.video.currentTime) {
        if (this.debug) {
          console.log(
            `[StormcloudVideoPlayer] Live break end — snapping to live edge: ${this.host.video.currentTime.toFixed(2)}s → ${liveSyncPos.toFixed(2)}s`
          );
        }
        this.host.video.currentTime = liveSyncPos;
      }

      if (this.host.video.paused) {
        if (this.debug) console.log("[StormcloudVideoPlayer] Content video paused in live mode after ads, resuming playback");
        this.host.video.play()?.catch(() => {});
      } else {
        if (this.debug) console.log("[StormcloudVideoPlayer] Content video already playing in live mode after ads");
      }
    } else if (this.host.video.paused) {
      this.host.video.play()?.catch(() => {});
    }
  }

  handleAdFailure(): void {
    this.timing.consecutiveFailures++;
    this.timing.recordGamNoFill();
    const remaining = this.timing.getRemainingAdMs();

    if (this.debug) {
      console.log(
        `[CONTINUOUS-FETCH] Ad failure: remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}, consecutiveFailures=${this.timing.consecutiveFailures}`,
        this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
      );
    }

    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) console.log(`[CONTINUOUS-FETCH] Max consecutive failures reached (${this.timing.consecutiveFailures}), ending ad break gracefully`);
      this.handleAdPodComplete();
      return;
    }

    if (this.timing.hasTimeToStartAnotherAd()) {
      this.tryNextAvailableAdWithRateLimit();
    } else {
      if (this.debug) console.log("[AD-ERROR] Ad failed and not enough time remaining for another ad. Failed URLs:", this.timing.failedVastUrls.size);
      this.handleAdPodComplete();
    }
  }
}
